/**
 * Spec 200 — WorkDetail full read-only surface (Fase 4 #2).
 *
 * Sections (mirrors the mobile detail screen minus write actions):
 *   - header with back, title, status pill
 *   - cliente: name + phone + address + tax id
 *   - notas internas
 *   - subtareas (list with ✓ for done)
 *   - fotos (grid; click → PhotoViewer)
 *   - voice notes (one VoiceNotePlayer per item)
 *   - signature (rendered SVG path)
 *   - statusHistory timeline
 *
 * Loads work + photos + voice notes in parallel from IndexedDB.
 * The mobile screen waits for permissions + thumbnails to load
 * before the photo grid; the web doesn't have that constraint
 * because images come from a `localPath` URL the relay-decrypt
 * pipeline already minted.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  CircleDot,
  Download,
  FileSignature,
  Image as ImageIcon,
  ListTodo,
  MessageSquare,
  Mic,
  User,
} from 'lucide-react';

import { InlineEditField } from '../components/InlineEditField';
import { PhoneInput } from '../components/PhoneInput';
import { PhotoUploadDropzone } from '../components/PhotoUploadDropzone';
import { PhotoViewer } from '../components/PhotoViewer';
import { SignatureCanvas } from '../components/SignatureCanvas';
import { SubtaskList, type Subtask } from '../components/SubtaskList';
import { TechnicianMultiSelect } from '../components/TechnicianMultiSelect';
import { VoiceNotePlayer } from '../components/VoiceNotePlayer';
import {
  getWorkById,
  getWorkPhotos,
  getWorkVoiceNotes,
  subscribeStore,
  type VoiceNote,
} from '../store/db';
import { broadcastWork } from '../sync/syncWork';
import { useWorkers } from '../sync/useWorkers';
import type { WebIdentity } from '../store/keys';
import type { Photo, Work } from '../types';
import {
  validateRequiredTitle,
  validateTaxId,
} from '../utils/validators';

import './WorkDetail.css';

const STATUS_LABEL: Record<Work['status'], string> = {
  pending: 'Pendiente',
  in_progress: 'En curso',
  completed: 'Completado',
};

const PRIORITY_LABEL: Record<'low' | 'normal' | 'high', string> = {
  low: 'Menor',
  normal: 'Normal',
  high: 'Mayor',
};

interface WorkDetailProps {
  identity: WebIdentity;
}

// E14 — validators centralised in `web/src/utils/validators.ts` so
// NewWork shares the exact same rules. Local references stay so the
// JSX below reads naturally.

/** Empty-state UI for the signature section: a single CTA that opens
 *  the in-browser SignatureCanvas (Path A). Path B (push to mobile)
 *  was dropped per user — viewing signatures is fine, requesting one
 *  remotely is not. */
function renderSignatureGate(args: {
  onCanvas: () => void;
}) {
  const { onCanvas } = args;
  return (
    <div className="wd-signature-actions" data-testid="workdetail-signature-actions">
      <button
        type="button"
        className="wd-signature-cta"
        onClick={onCanvas}
        data-testid="workdetail-signature-open"
      >
        <FileSignature size={16} strokeWidth={2} aria-hidden="true" />
        Firmar aquí
      </button>
    </div>
  );
}

export function WorkDetail({ identity }: WorkDetailProps) {
  const navigate = useNavigate();
  const { workId } = useParams<{ workId: string }>();
  const workers = useWorkers();

  const [work, setWork] = useState<Work | null | undefined>(undefined);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [voiceNotes, setVoiceNotes] = useState<VoiceNote[]>([]);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  // F17 — opens the SignatureCanvas modal. The captured SVG path
  // is persisted via `patchWork({ signature, signedAt })` from the
  // onSave callback below, so the rest of the screen reacts to it
  // through the existing store-change subscription.
  const [signatureOpen, setSignatureOpen] = useState(false);
  // F17 PR2 — Path B request state. 'idle' shows the dual button
  useEffect(() => {
    if (!workId) return;
    let cancelled = false;
    async function reload() {
      // Load all three in parallel — they hit different stores so
      // there's no contention; the slowest dominates.
      const [w, ps, vns] = await Promise.all([
        getWorkById(workId!),
        getWorkPhotos(workId!),
        getWorkVoiceNotes(workId!),
      ]);
      if (cancelled) return;
      setWork(w);
      setPhotos(ps);
      setVoiceNotes(vns);
    }
    reload();
    // Live sync: when the relay delivers a new revision of this
    // work, re-fetch and re-render. Subscribing across the whole
    // store (rather than per-id) keeps the API tiny; the cost is
    // one extra reload per unrelated mutation, which is negligible
    // for the cache-hot path.
    const off = subscribeStore(() => {
      void reload();
    });
    return () => {
      cancelled = true;
      off();
    };
  }, [workId]);

  const subtaskSummary = useMemo(() => {
    if (!work?.subtasks?.length) return null;
    const done = work.subtasks.filter((s) => s.done).length;
    return `${done} / ${work.subtasks.length}`;
  }, [work]);

  /**
   * Optimistic-update helper: applies a patch to the local Work state,
   * persists via broadcastWork (which also fires the relay broadcast),
   * and lets the store-change emitter pick the change back up to
   * propagate to other mounts. The returned promise resolves with the
   * stamped Work — useful for follow-up edits in the same handler.
   */
  async function patchWork(patch: Partial<Work>): Promise<void> {
    if (!work) return;
    const next: Work = { ...work, ...patch };
    setWork(next); // optimistic
    await broadcastWork(next, identity);
  }

  /**
   * Builds the PDF in-process and triggers a browser download. Anchored
   * to a transient Blob URL that we revoke right after the click so the
   * GC can reclaim the bytes. Synchronous from the user's perspective:
   * jsPDF runs on the main thread, and a typical work renders in well
   * under 100ms even with several photos.
   */
  async function handleExportPdf(): Promise<void> {
    if (!work) return;
    const { generateWorkPdf } = await import('../utils/pdfWork');
    const { blob, filename } = await generateWorkPdf({
      work,
      photos,
      voiceNotes,
      identity,
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  if (work === undefined) {
    return (
      <div className="wd-shell" data-testid="workdetail-screen">
        <p className="wd-loading" data-testid="workdetail-loading">
          Cargando…
        </p>
      </div>
    );
  }

  if (work === null) {
    return (
      <div className="wd-shell" data-testid="workdetail-screen">
        <header className="wd-header">
          <button
            type="button"
            className="wd-back"
            onClick={() => navigate(-1)}
            data-testid="workdetail-back"
          >
            <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
            Volver
          </button>
        </header>
        <p className="wd-empty" data-testid="workdetail-not-found">
          Trabajo no encontrado.
        </p>
      </div>
    );
  }

  return (
    <div className="wd-shell" data-testid="workdetail-screen">
      <header className="wd-header">
        <button
          type="button"
          className="wd-back"
          onClick={() => navigate(-1)}
          data-testid="workdetail-back"
        >
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Volver
        </button>
        <button
          type="button"
          className="wd-export"
          onClick={handleExportPdf}
          data-testid="workdetail-export-pdf"
          aria-label="Exportar a PDF"
        >
          <Download size={20} strokeWidth={2} aria-hidden="true" />
          Exportar PDF
        </button>
      </header>

      <main id="main-content" className="wd-main" data-testid="workdetail-content" tabIndex={-1}>
        <div className="wd-col-left">
        <section className="wd-title-row">
          <h1 className="wd-title" data-testid="workdetail-title">
            <InlineEditField
              value={work.title}
              onSave={(next) => patchWork({ title: next })}
              validate={validateRequiredTitle}
              placeholder="Sin título"
              testId="workdetail-title-edit"
              ariaLabel="Título del trabajo"
              as="span"
            />
          </h1>
        </section>

        <section className="wd-section">
          {/* Wrapper is a <div>, not a <p>: InlineEditField in
              edit mode renders a <div>+<textarea>, which would be
              invalid nested inside a paragraph. The visual styling
              is unchanged (CSS rules don't depend on the tag). */}
          <div className="wd-description" data-testid="workdetail-description">
            <InlineEditField
              value={work.description ?? ''}
              onSave={(next) => patchWork({ description: next || undefined })}
              multiline
              placeholder="Sin descripción"
              testId="workdetail-description-edit"
              ariaLabel="Descripción del trabajo"
            />
          </div>
        </section>

        {/* Notas ───────────────────────────────────────────────── */}
        <section className="wd-section" data-testid="workdetail-notes">
          <h2 className="wd-h2">
            <MessageSquare size={20} strokeWidth={2} aria-hidden="true" />
            Notas internas
          </h2>
          <div className="wd-notes">
            <InlineEditField
              value={work.notes ?? ''}
              onSave={(next) => patchWork({ notes: next || undefined })}
              multiline
              placeholder="Sin notas"
              testId="workdetail-notes-edit"
              ariaLabel="Notas internas"
            />
          </div>
        </section>

        {/* Subtareas ───────────────────────────────────────────── */}
        <section className="wd-section" data-testid="workdetail-subtasks">
          <h2 className="wd-h2">
            <ListTodo size={20} strokeWidth={2} aria-hidden="true" />
            Subtareas
            {subtaskSummary && <span className="wd-h2-sub"> · {subtaskSummary}</span>}
          </h2>
          <SubtaskList
            subtasks={(work.subtasks ?? []) as Subtask[]}
            onChange={(next) =>
              patchWork({ subtasks: next.length > 0 ? next : undefined })
            }
            testId="workdetail-subtasks-list"
          />
        </section>

        {/* Historial de estado ─────────────────────────────────── */}
        {work.statusHistory && work.statusHistory.length > 0 && (
          <section className="wd-section" data-testid="workdetail-history">
            <h2 className="wd-h2">Historial de estado</h2>
            <ol className="wd-history">
              {work.statusHistory.map((h, i) => (
                <li
                  key={`${h.at}-${i}`}
                  data-testid={`workdetail-history-${i}`}
                  className="wd-history-item"
                >
                  <span className={`wd-status wd-status--${h.status}`}>
                    {STATUS_LABEL[h.status]}
                  </span>
                  <span className="wd-history-meta">
                    {h.name && <span>{h.name} · </span>}
                    {new Date(h.at).toLocaleString('es', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}
        </div>
        {/* ── Right column ──────────────────────────────────────── */}
        <div className="wd-col-right">
        {/* Cliente ─────────────────────────────────────────────── */}
        {/* Spec 200 Fase 5 #1 — every client field is editable in
            place, even on a fresh work that has no client info yet
            (placeholder rows let the user fill them in). */}
        <section className="wd-section" data-testid="workdetail-client">
          <h2 className="wd-h2">
            <User size={20} strokeWidth={2} aria-hidden="true" />
            Cliente
          </h2>
          <dl className="wd-kv">
            <dt>Nombre</dt>
            <dd>
              <InlineEditField
                value={work.clientName ?? ''}
                onSave={(next) => patchWork({ clientName: next || undefined })}
                placeholder="Añadir nombre"
                testId="workdetail-client-name-edit"
                ariaLabel="Nombre del cliente"
              />
            </dd>
            <dt>Teléfono</dt>
            <dd>
              {/* Spec 219 — phone field uses the dedicated PhoneInput
                  so the flag picker, dial code and E.164 normalisation
                  match the cross-stack contract. */}
              <PhoneInput
                value={work.clientPhone ?? ''}
                onChange={(next) => {
                  // Guard against the lib's onMount onChange that
                  // would otherwise trigger a redundant broadcastWork
                  // for every detail mount.
                  const current = work.clientPhone ?? '';
                  if (current === (next || '')) return;
                  void patchWork({ clientPhone: next || undefined });
                }}
                testId="workdetail-client-phone-edit"
                ariaLabel="Teléfono del cliente"
                placeholder="Añadir teléfono"
              />
            </dd>
            <dt>Dirección</dt>
            <dd>
              <InlineEditField
                value={work.clientAddress ?? ''}
                onSave={(next) => patchWork({ clientAddress: next || undefined })}
                placeholder="Añadir dirección"
                testId="workdetail-client-address-edit"
                ariaLabel="Dirección del cliente"
              />
            </dd>
            <dt>NIF/CIF</dt>
            <dd>
              <InlineEditField
                value={work.clientTaxId ?? ''}
                onSave={(next) =>
                  patchWork({ clientTaxId: next ? next.toUpperCase() : undefined })
                }
                validate={validateTaxId}
                placeholder="Añadir NIF/CIF"
                testId="workdetail-client-taxid-edit"
                ariaLabel="NIF o CIF del cliente"
              />
            </dd>
          </dl>
        </section>

        {/* Equipo (Fase 5 #5) ─────────────────────────────────── */}
        <section className="wd-section" data-testid="workdetail-team">
          <h2 className="wd-h2">Equipo asignado</h2>
          <TechnicianMultiSelect
            value={work.assignedTechnicians ?? []}
            workers={workers}
            onChange={(next) =>
              patchWork({ assignedTechnicians: next.length > 0 ? next : undefined })
            }
            testId="workdetail-team-select"
          />
        </section>

        {/* Status switcher (Fase 5 #3, B9 affordance) ─────────────── */}
        {/* Three buttons styled like the read-only status chip but
            interactive. The active status renders solid; the others
            render hollow. Each click appends to statusHistory with
            `{ status, name: identity.name, at: now }` so the timeline
            below grows in sync. We don't restrict transitions —
            mobile lets workers go from completed back to in_progress
            (an "actually it isn't done" correction is a routine
            mid-job event), so we mirror that.

            B9 — the user reported the row didn't read as editable:
            the chips looked like display-only badges. Now wrapped in
            a `wd-section` with a "Estado" heading + a "Pulsa para
            cambiar" hint so the affordance is explicit; the chip
            hover state was also strengthened (see WorkDetail.css). */}
        <section className="wd-section" data-testid="workdetail-status-section">
          <h2 className="wd-h2">
            <CircleDot size={20} strokeWidth={2} aria-hidden="true" />
            Estado
            <span className="wd-h2-sub">· Pulsa una opción para cambiarlo</span>
          </h2>
          <div
            className="wd-status-switcher"
            data-testid="workdetail-status-switcher"
            role="radiogroup"
            aria-label="Estado del trabajo"
          >
            {(['pending', 'in_progress', 'completed'] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={`wd-status-btn wd-status-btn--${s} ${
                  work.status === s ? 'wd-status-btn--active' : ''
                }`}
                data-testid={`workdetail-status-${s}`}
                role="radio"
                aria-checked={work.status === s}
                aria-pressed={work.status === s}
                onClick={() => {
                  if (work.status === s) return; // no-op on the active one
                  const event = {
                    status: s,
                    name: identity.name,
                    at: Date.now(),
                  };
                  void patchWork({
                    status: s,
                    statusHistory: [...(work.statusHistory ?? []), event],
                  });
                }}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </section>

        {/* Urgencia ──────────────────────────────────────────────
            Same chip pattern as Estado, but no history trail. The
            field is optional on Work; `undefined` is read as `normal`. */}
        <section className="wd-section" data-testid="workdetail-priority-section">
          <h2 className="wd-h2">
            <CircleDot size={20} strokeWidth={2} aria-hidden="true" />
            Urgencia
            <span className="wd-h2-sub">· Pulsa una opción para cambiarla</span>
          </h2>
          <div
            className="wd-priority-switcher"
            data-testid="workdetail-priority-switcher"
            role="radiogroup"
            aria-label="Urgencia del trabajo"
          >
            {(['low', 'normal', 'high'] as const).map((p) => {
              const current = work.priority ?? 'normal';
              const active = current === p;
              return (
                <button
                  key={p}
                  type="button"
                  className={`wd-priority-btn wd-priority-btn--${p} ${
                    active ? 'wd-priority-btn--active' : ''
                  }`}
                  data-testid={`workdetail-priority-${p}`}
                  role="radio"
                  aria-checked={active}
                  aria-pressed={active}
                  onClick={() => {
                    if (active) return;
                    void patchWork({ priority: p });
                  }}
                >
                  {PRIORITY_LABEL[p]}
                </button>
              );
            })}
          </div>
        </section>

        {/* Fotos ───────────────────────────────────────────────── */}
        {/* F16 — section always renders so the dropzone is reachable
            even on works without prior photos. The grid is hidden
            when empty; the dropzone handles its own empty state. */}
        <section className="wd-section" data-testid="workdetail-photos">
          <h2 className="wd-h2">
            <ImageIcon size={20} strokeWidth={2} aria-hidden="true" />
            Fotos{photos.length > 0 && ` · ${photos.length}`}
          </h2>
          {photos.length > 0 && (
            <div className="wd-photo-grid">
              {photos.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  className="wd-photo-thumb"
                  onClick={() => setViewerIndex(i)}
                  data-testid={`workdetail-photo-${p.id}`}
                  aria-label={`Foto ${i + 1} de ${photos.length}`}
                >
                  <img src={p.localPath} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
          <PhotoUploadDropzone
            workId={work.id}
            identity={identity}
            onUploaded={(uploaded) =>
              setPhotos((prev) =>
                prev.some((p) => p.id === uploaded.id)
                  ? prev.map((p) => (p.id === uploaded.id ? uploaded : p))
                  : [uploaded, ...prev],
              )
            }
          />
        </section>

        {/* Notas de voz ────────────────────────────────────────── */}
        {/* Only render players for voice notes whose decrypt
            pipeline has produced a localPath. Notes still in flight
            surface as a "Descifrando…" line so the UI never shows a
            broken <audio> control with src="". */}
        {voiceNotes.length > 0 && (
          <section className="wd-section" data-testid="workdetail-voice-notes">
            <h2 className="wd-h2">
              <Mic size={20} strokeWidth={2} aria-hidden="true" />
              Notas de voz · {voiceNotes.length}
            </h2>
            <div className="wd-voice-list">
              {voiceNotes
                .filter((vn) => vn.localPath)
                .map((vn) => (
                  <VoiceNotePlayer
                    key={vn.id}
                    src={vn.localPath ?? ''}
                    name={vn.name}
                    durationMs={vn.durationMs}
                    recordedAt={vn.recordedAt}
                  />
                ))}
              {voiceNotes.some((vn) => !vn.localPath) && (
                <p
                  className="wd-voice-pending"
                  data-testid="workdetail-voice-pending"
                >
                  Algunas notas se están descifrando…
                </p>
              )}
            </div>
          </section>
        )}

        {/* Firma ───────────────────────────────────────────────── */}
        {/* F17 — section always renders. When there's no captured
            signature yet, a "Firmar aquí" button opens
            SignatureCanvas; once captured, the existing render
            takes over. viewBox widened to 600×240 to match what
            the canvas component captures (spec 220 §3.1.3) — older
            mobile-captured paths still render correctly because
            their coordinates fit within the larger box (just with
            extra whitespace, never clipped). */}
        <section className="wd-section" data-testid="workdetail-signature">
          <h2 className="wd-h2">
            <FileSignature size={20} strokeWidth={2} aria-hidden="true" />
            Firma del cliente
          </h2>
          {work.signature ? (
            <>
              <div className="wd-signature-frame" data-testid="workdetail-signature-rendered">
                <svg
                  viewBox="0 0 600 240"
                  xmlns="http://www.w3.org/2000/svg"
                  className="wd-signature-svg"
                  role="img"
                  aria-label="Firma del cliente"
                >
                  <path
                    d={work.signature}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              {work.signedAt && (
                <p className="wd-signature-date">
                  Firmado el{' '}
                  {new Date(work.signedAt).toLocaleString('es', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
              <button
                type="button"
                className="wd-signature-replace"
                onClick={() => setSignatureOpen(true)}
                data-testid="workdetail-signature-replace"
              >
                Capturar nueva firma
              </button>
            </>
          ) : (
            renderSignatureGate({
              onCanvas: () => setSignatureOpen(true),
            })
          )}
        </section>

        </div>
      </main>

      {viewerIndex !== null && (
        <PhotoViewer
          photos={photos}
          initialIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}

      <SignatureCanvas
        open={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        onSave={(pathSvg) => {
          void patchWork({ signature: pathSvg, signedAt: Date.now() });
        }}
      />
    </div>
  );
}
